import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  AppWindow,
  Bell,
  DollarSign,
  Percent,
  UserCircle,
  Hexagon,
  Home,
  List,
  LogOut,
  Menu,
  Settings,
  ShieldAlert,
  Store,
  Wallet,
  User,
  X,
} from 'lucide-react';
import { SupportDownloadRail } from '@kioskgaming/ui';
import { useAuth } from '../../hooks/useAuth';
import { AGENT_AUTH_TOKEN_CHANGED_EVENT } from '../../constants';
import { fetchDashboard } from '../../services/api';
import AgentNotificationBell from '../AgentNotificationBell';
import AgentInAppPopupGate from '../AgentInAppPopupGate';
import VerifyNowTrigger from '../VerifyNowTrigger';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  matchPrefix?: string;
  primary?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Home', icon: Home, end: true, primary: true },
  { to: '/shops', label: 'Shops', icon: Store, matchPrefix: '/shops', primary: true },
  { to: '/players', label: 'Players', icon: User, end: true, primary: true },
  { to: '/transactions', label: 'Transactions', icon: List, end: true, primary: true },
  { to: '/cash-transactions', label: 'Cash transactions', icon: Hexagon, matchPrefix: '/cash-transactions', primary: false },
  { to: '/online-transactions', label: 'USD wallet transactions', icon: Wallet, end: true, primary: false },
  { to: '/provider-fees', label: 'Fee Configuration', icon: Percent, end: true, primary: false },
  { to: '/fraud-indicators', label: 'Fraud indicators', icon: ShieldAlert, end: true, primary: false },
  { to: '/popups/send', label: 'Send popup', icon: AppWindow, end: true },
  { to: '/broadcasts/send', label: 'Send broadcast', icon: Bell, end: true },
  { to: '/settings', label: 'Settings', icon: Settings, end: true },
];

function SidebarBalanceCards({
  creditBalance,
  usdBalance,
  loading,
}: {
  creditBalance: number | null;
  usdBalance: number | null;
  loading: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Current credit balance</p>
              <p className="mt-0.5 text-base font-bold tabular-nums text-gray-900">
                {loading ? '…' : `$${Number(creditBalance ?? 0).toFixed(2)}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <DollarSign className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Current USD balance</p>
              <p className="mt-0.5 text-base font-bold tabular-nums text-gray-900">
                {loading ? '…' : `$${Number(usdBalance ?? 0).toFixed(2)}`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function isPathActive(pathname: string, item: NavItem) {
  if (item.matchPrefix) return pathname === item.matchPrefix || pathname.startsWith(`${item.matchPrefix}/`);
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function AgentProfileMenu({ menuAlign = 'right' }: { menuAlign?: 'left' | 'right' }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const label = user?.name || user?.email || 'Account';

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={`Account menu — ${label}`}
      >
        <UserCircle className="h-5 w-5" strokeWidth={1.5} aria-hidden />
      </button>
      {open && (
        <div
          className={`absolute top-full z-50 mt-1.5 min-w-[220px] rounded-xl border border-gray-200 bg-white py-1 shadow-lg ${
            menuAlign === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <p className="truncate border-b border-gray-100 px-3 py-2 text-xs text-gray-500">{user?.email}</p>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

function SidebarNavLink({
  to,
  end,
  matchPrefix,
  icon: Icon,
  children,
  onClick,
}: {
  to: string;
  end?: boolean;
  matchPrefix?: string;
  icon: LucideIcon;
  children: ReactNode;
  onClick?: () => void;
}) {
  const location = useLocation();
  const active = matchPrefix
    ? location.pathname === matchPrefix || location.pathname.startsWith(`${matchPrefix}/`)
    : end
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
        active ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-gray-900' : 'text-gray-500'}`} strokeWidth={1.5} />
      <span>{children}</span>
    </NavLink>
  );
}

export type AppMainContentWidth = 'fluid' | 'contained';

export default function AppLayout({
  children,
  contentWidth = 'fluid',
}: {
  children: ReactNode;
  /**
   * fluid: full-width main column (default), responsive padding.
   * contained: max-w-6xl centered — use only when a page intentionally needs a narrow column.
   */
  contentWidth?: AppMainContentWidth;
}) {
  const telegramSupportUrl = process.env.REACT_APP_CLIENT_TELEGRAM_SUPPORT_URL?.trim() || undefined;
  const androidAppUrl = process.env.REACT_APP_CLIENT_ANDROID_APK_URL?.trim() || undefined;
  const iosGuideUrl = process.env.REACT_APP_CLIENT_IOS_VIDEO_URL?.trim() || undefined;
  const { user } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [popupNavAllowed, setPopupNavAllowed] = useState<boolean | null>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [usdBalance, setUsdBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const loadPopupNav = useCallback(async () => {
    if (!user?.id) {
      setPopupNavAllowed(null);
      setCreditBalance(null);
      setUsdBalance(null);
      setBalanceLoading(false);
      return;
    }
    setBalanceLoading(true);
    try {
      const res = await fetchDashboard();
      if (res.success && res.data) {
        setPopupNavAllowed(!!res.data.can_delegate_popups);
        setCreditBalance(res.data.credit_balance);
        setUsdBalance(res.data.online_balance_net ?? null);
      } else {
        setPopupNavAllowed(false);
        setCreditBalance(null);
        setUsdBalance(null);
      }
    } catch {
      setPopupNavAllowed(false);
      setCreditBalance(null);
      setUsdBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (typeof user?.can_delegate_popups === 'boolean') {
      setPopupNavAllowed(user.can_delegate_popups);
    }
  }, [user]);

  useEffect(() => {
    void loadPopupNav();
  }, [loadPopupNav]);

  useEffect(() => {
    const onAuth = () => {
      void loadPopupNav();
    };
    window.addEventListener(AGENT_AUTH_TOKEN_CHANGED_EVENT, onAuth);
    return () => window.removeEventListener(AGENT_AUTH_TOKEN_CHANGED_EVENT, onAuth);
  }, [loadPopupNav]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && user) void loadPopupNav();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadPopupNav, user]);

  const navItemsFiltered = useMemo(() => {
    if (popupNavAllowed !== true) {
      return NAV_ITEMS.filter((i) => i.to !== '/popups/send' && i.to !== '/broadcasts/send');
    }
    return NAV_ITEMS;
  }, [popupNavAllowed]);

  const primaryMobileNav = useMemo(() => navItemsFiltered.filter((item) => item.primary), [navItemsFiltered]);

  const closeMenus = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="flex min-h-screen bg-[#fafafa] text-gray-900">
      <AgentInAppPopupGate />

      <aside className="hidden w-[280px] shrink-0 border-r border-gray-200 bg-white lg:flex lg:flex-col">
        <div className="flex flex-1 flex-col gap-6 p-5">
          <div className="flex items-center gap-2.5 px-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-900 text-white">
              <Hexagon className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="leading-tight">
              <span className="block text-lg font-bold tracking-tight">Kiosk Gaming</span>
              <span className="text-xs font-medium text-gray-500">Agent</span>
            </div>
          </div>

          <SidebarBalanceCards creditBalance={creditBalance} usdBalance={usdBalance} loading={balanceLoading} />

          <nav className="flex flex-1 flex-col gap-1 overflow-y-auto pr-1">
            {navItemsFiltered.map((item) => (
              <SidebarNavLink key={item.to} to={item.to} end={item.end} matchPrefix={item.matchPrefix} icon={item.icon}>
                {item.label}
              </SidebarNavLink>
            ))}
          </nav>
        </div>
      </aside>

      {mobileMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/35 lg:hidden"
            aria-label="Close navigation menu"
            onClick={closeMenus}
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-[86%] max-w-xs flex-col border-r border-gray-200 bg-white p-4 lg:hidden">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-900 text-white">
                  <Hexagon className="h-4.5 w-4.5" strokeWidth={1.75} />
                </div>
                <div className="text-sm font-semibold">Kiosk Agent</div>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
                onClick={closeMenus}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-3">
              <SidebarBalanceCards creditBalance={creditBalance} usdBalance={usdBalance} loading={balanceLoading} />
            </div>

            <nav className="mt-4 flex flex-1 flex-col gap-1 overflow-y-auto pb-4">
              {navItemsFiltered.map((item) => (
                <SidebarNavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  matchPrefix={item.matchPrefix}
                  icon={item.icon}
                  onClick={closeMenus}
                >
                  {item.label}
                </SidebarNavLink>
              ))}
            </nav>
          </aside>
        </>
      )}

      <main className="relative min-w-0 flex-1 pb-24 lg:pb-0">
        <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur lg:hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-3">
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1 text-center">
              <p className="truncate text-sm font-semibold">{user?.name || user?.email || 'Agent Portal'}</p>
              <p className="text-xs text-gray-500">Agent</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <VerifyNowTrigger />
              <AgentNotificationBell />
              <AgentProfileMenu menuAlign="right" />
            </div>
          </div>
        </header>

        <div className="pointer-events-none absolute right-4 top-4 z-30 hidden lg:flex lg:items-center lg:gap-2">
          <div className="pointer-events-auto flex items-center gap-2">
            <VerifyNowTrigger />
            <AgentNotificationBell />
            <AgentProfileMenu menuAlign="right" />
          </div>
        </div>

        <div
          className={
            contentWidth === 'contained'
              ? 'mx-auto w-full min-w-0 max-w-6xl px-4 py-5 sm:px-6 lg:px-8 lg:pb-8 lg:pt-16'
              : 'w-full min-w-0 max-w-none px-4 py-5 sm:px-6 lg:pb-8 lg:pl-6 lg:pr-14 lg:pt-16 xl:pl-8 xl:pr-16'
          }
        >
          {children}
        </div>
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 px-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-2 shadow-[0_-8px_20px_rgba(0,0,0,0.06)] backdrop-blur lg:hidden">
        <div
          className="grid gap-1"
          style={{ gridTemplateColumns: `repeat(${Math.max(primaryMobileNav.length, 1)}, minmax(0, 1fr))` }}
        >
          {primaryMobileNav.map((item) => {
            const Icon = item.icon;
            const active = isPathActive(location.pathname, item);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={`flex min-h-11 flex-col items-center justify-center gap-1 rounded-lg px-1 py-1 text-[11px] font-medium ${
                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-600'
                }`}
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
      <SupportDownloadRail telegramUrl={telegramSupportUrl} androidUrl={androidAppUrl} iosUrl={iosGuideUrl} />
    </div>
  );
}
